import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import {
  CREATE_REFERRAL_LINK,
  createReferralLink,
  GET_REFERRAL_LIST_ACTION,
  GET_REFERRAL_PAYOUTS_ACTION,
  GET_REFERRAL_SUMMARY_ACTION,
  getReferralPayouts,
} from '~/actions';
import { Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Box, CardSpinner, Center, Column, Pressable, Row } from '~/commonComponents';
import { IReferrer, useGetReferrer } from '~/domain/Referral';
import { useGetSelectedOffice } from '~/domain/SelectedOffice';
import { useIsReferralDisabledForState, useLoading, useUserSelector } from '~/hooks';
import { useAppSelector } from '~/hooks/useAppSelector';
import { IReferral } from '~/models';
import { routes } from '~/routes';
import { GotReferredSection } from './components/GotReferredSection';
import { InputReferralLink } from './components/GotReferredSection/components/InputReferralLink/InputReferralLink';
import { InstructionItem } from './components/InstructionItem';
import { ReferralLink } from './components/ReferralLink';
import { ReferralLinkModals } from './components/ReferralLinkModals';
import { ReferralList } from './components/ReferralList';
import { ReferralSummary } from './components/ReferralSummary';
import { useApplyReferralLink } from './hooks/useApplyReferralLink';
import { useReferralList } from './hooks/useReferralList';

import ReferralGiftCard from '~/images/referral/referral-gift-card.svg';
import GiftIcon from '~/images/referral/referral-gift-icon.svg';
import PeopleIcon from '~/images/referral/referral-people-icon.svg';
import ShareIcon from '~/images/referral/referral-share-icon.svg';

const loadingActions = [
  CREATE_REFERRAL_LINK,
  GET_REFERRAL_LIST_ACTION,
  GET_REFERRAL_SUMMARY_ACTION,
  GET_REFERRAL_PAYOUTS_ACTION,
];

export const ReferralScreenV2 = () => {
  const dispatch = useDispatch();
  const user = useUserSelector();
  const mutateGetSelectedOffice = useGetSelectedOffice(user?.id);
  const { isLoading } = useLoading(loadingActions);
  const { totalUserReferred, totalEarned, referralList, fetchReferralList } = useReferralList();
  const { data: referrer, isLoading: isReferrerLoading } = useGetReferrer();

  const {
    shortLink,
    showPopup,
    isLoading: isLoadingApply,
    handleClosePopup,
    isSuccess: isLinkingSuccess,
    submitReferralLink,
    errorMessage,
  } = useApplyReferralLink();

  const isReferralProgramEnabledForUserState = !useIsReferralDisabledForState();
  const isGotReferredSectionEnabled =
    isReferralProgramEnabledForUserState &&
    !user.referralId &&
    !user?.completedShiftCount;

  useEffect(() => {
    mutateGetSelectedOffice.mutateAsync();
    dispatch(createReferralLink());
    dispatch(getReferralPayouts());
    fetchReferralList();
  }, [dispatch]);

  if (isLoading || isReferrerLoading) {
    return <CardSpinner />;
  }

  if (isGotReferredSectionEnabled) {
    return (
      <Column flex={1}>
        <Box className="component_container" flexDirection="initial">
          <Box className="floating_container" padding={40}>
            <Center paddingBottom={40}>
              <Typography as="span" bold textAlign="center" size="heading_2" style={{ marginTop: 32 }}>
                Referral Program
              </Typography>

              <img
                src={ReferralGiftCard}
                style={{ width: 203, height: 169, paddingTop: 32 }}
                alt="icon"
              />

              <Center marginTop={8}>
                <GotReferredSection />
                <InputReferralLink
                  shortLink={shortLink}
                  isLinkingSuccess={isLinkingSuccess}
                  isLoading={isLoadingApply}
                  onSubmit={submitReferralLink}
                  errorMessage={errorMessage}
                />
              </Center>
            </Center>
          </Box>
        </Box>

        <Box className="component_container" flexDirection="initial">
          <Box className="floating_container" padding={40} marginTop={-12}>
            <Center>
              <InviteFriends
                totalUserReferred={totalUserReferred}
                totalEarned={totalEarned}
                referralList={referralList}
                referrer={referrer}
              />
            </Center>
          </Box>
        </Box>

        <ReferralLinkModals
          showPopup={showPopup}
          onClosePopup={handleClosePopup}
        />
      </Column>
    );
  }

  return (
    <>
      <Box className="component_container" flexDirection="initial">
        <Box className="floating_container" padding={40}>
          <Center>
            <Header />

            <InviteFriends
              totalUserReferred={totalUserReferred}
              totalEarned={totalEarned}
              referralList={referralList}
              referrer={referrer}
            />
          </Center>
        </Box>
      </Box>

      <ReferralLinkModals
        showPopup={showPopup}
        onClosePopup={handleClosePopup}
      />
    </>
  );
};

const Header = () => {
  return (
    <Center>
      <Typography as="span" bold textAlign="center" size="heading_2" style={{ marginTop: 32 }}>
        Referral Program
      </Typography>

      <img src={ReferralGiftCard} style={{ width: 203, height: 169, paddingTop: 32 }} alt="icon" />
    </Center>
  );
};

const InviteFriends = ({
  totalUserReferred,
  totalEarned,
  referralList,
  referrer,
}: {
  totalUserReferred: number | null;
  totalEarned: number | null;
  referralList: IReferral[];
  referrer: IReferrer | null;
}) => {
  const navigate = useNavigate();
  const { referralLink } = useAppSelector((state) => state.user);

  return (
    <>
      <Typography as="span" color={colors.primary[500]} bold size="heading_4" style={{ paddingTop: 32 }}>
        Invite Friends,{' '}
        <Typography as="span" color={colors.success[400]} bold size="heading_4">
          Get an E-Gift Card!
        </Typography>
      </Typography>

      <Box marginBottom={24}>
        <ReferralLink link={referralLink || ''} style={{ marginTop: 32 }} />
      </Box>

      <Row gap={32} paddingTop={26} width={960} justifyContent="space-between">
        <InstructionItem
          icon={ShareIcon}
          title="1. Share Your Code"
          description={
            <>
              Send your link to as many friends or
              <br />
              colleagues as you like!
            </>
          }
        />

        <InstructionItem
          icon={PeopleIcon}
          title="2. Your Friend Completes a Shift"
          description={
            <>
              Once your friend completes their first shift
              <br />
              successfully, we’ll notify you both.
            </>
          }
        />

        <InstructionItem
          icon={GiftIcon}
          title="3. You Both Get Rewards!"
          description={
            <>
              You’ll receive your gift card via
              <br />
              your associated email.
            </>
          }
        />
      </Row>

      <Box height={1} width="70%" backgroundColor={colors.neutral[100]} marginTop={40} />

      {referralList?.length > 0 ? (
        <ReferralSummary
          totalReferrals={totalUserReferred || 0}
          totalEarned={totalEarned || 0}
          style={{ marginTop: 32 }}
        />
      ) : null}

      <ReferralList referrer={referrer} data={referralList} />

      <Pressable onPress={() => navigate(routes.referralDisclaimer)} style={{ paddingBlock: 40 }}>
        <Typography as="span" bold color={colors.secondary[500]}>
          Disclaimer/ Terms and Conditions
        </Typography>
      </Pressable>
    </>
  );
};
